import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Trash2, Save, AlertTriangle, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_CARDS = 300;

export default function EditSetPage() {
    const { setId } = useParams();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        fetchSet();
    }, [setId]);

    const fetchSet = async () => {
        try {
            const response = await axios.get(`${API}/sets/${setId}`);
            setTitle(response.data.title);
            setCards(response.data.flashcards.map(fc => ({
                id: fc.id,
                term: fc.term,
                definition: fc.definition,
                isNew: false
            })));
        } catch (err) {
            setError('Failed to load set');
        } finally {
            setLoading(false);
        }
    };

    const addCard = async () => {
        if (cards.length >= MAX_CARDS) return;
        
        const newCard = { 
            id: crypto.randomUUID(), 
            term: '', 
            definition: '',
            isNew: true 
        };
        setCards([...cards, newCard]);
    };

    const removeCard = async (cardId, isNew) => {
        if (cards.length <= 1) return;
        
        if (!isNew) {
            try {
                await axios.delete(`${API}/sets/${setId}/flashcards/${cardId}`);
            } catch (err) {
                setError('Failed to delete card');
                return;
            }
        }
        setCards(cards.filter(c => c.id !== cardId));
    };

    const updateCard = (id, field, value) => {
        setCards(cards.map(c => 
            c.id === id ? { ...c, [field]: value } : c
        ));
    };

    const handleSave = async () => {
        setError('');
        setSaved(false);

        if (!title.trim()) {
            setError('Please enter a title');
            return;
        }

        setSaving(true);
        try {
            // Update title
            await axios.put(`${API}/sets/${setId}`, { title: title.trim() });

            // Save new cards
            for (const card of cards) {
                if (card.isNew && card.term.trim() && card.definition.trim()) {
                    await axios.post(`${API}/sets/${setId}/flashcards`, {
                        term: card.term.trim(),
                        definition: card.definition.trim()
                    });
                } else if (!card.isNew && card.term.trim() && card.definition.trim()) {
                    await axios.put(`${API}/sets/${setId}/flashcards/${card.id}`, {
                        term: card.term.trim(),
                        definition: card.definition.trim()
                    });
                }
            }

            // Refresh data
            await fetchSet();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const isAtLimit = cards.length >= MAX_CARDS;
    const validCardCount = cards.filter(c => c.term.trim() && c.definition.trim()).length;

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="pt-36 pb-16 px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="animate-pulse space-y-4">
                            <div className="h-12 bg-muted w-1/2" />
                            <div className="h-8 bg-muted w-full" />
                            <div className="h-8 bg-muted w-full" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* Sticky Top Bar */}
            <div className="fixed top-16 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
                <div className="max-w-4xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-button"
                            className="rounded-none"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                            Back
                        </Button>

                        <div className="flex items-center gap-4">
                            <span 
                                data-testid="card-counter"
                                className={`font-mono text-xs tracking-wider ${isAtLimit ? 'text-destructive' : 'text-muted-foreground'}`}
                            >
                                {cards.length}/{MAX_CARDS}
                            </span>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="save-set-button"
                                className={`rounded-none btn-lift ${saved ? 'bg-green-600 hover:bg-green-600' : ''}`}
                            >
                                {saved ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Saved!
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        {saving ? 'Saving...' : 'Save'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <main className="pt-36 pb-16 px-6">
                <div className="max-w-4xl mx-auto">
                    {/* Title Input */}
                    <div className="mb-12">
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter set title..."
                            data-testid="set-title-input"
                            className="w-full font-serif text-3xl md:text-4xl tracking-tight bg-transparent border-0 border-b-2 border-border focus:border-primary focus:outline-none pb-4 placeholder:text-muted-foreground/50"
                        />
                        <p className="mt-2 text-sm text-muted-foreground">
                            {validCardCount} card{validCardCount !== 1 ? 's' : ''} in this set
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div 
                            data-testid="edit-error"
                            className="mb-8 p-4 bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-center gap-2"
                        >
                            <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                            {error}
                        </div>
                    )}

                    {/* Cards List */}
                    <div className="space-y-0">
                        {cards.map((card, index) => (
                            <div 
                                key={card.id}
                                data-testid={`card-row-${index}`}
                                className={`card-list-row ${card.isNew ? 'bg-primary/5' : ''}`}
                            >
                                <div className="col-span-1 font-mono text-xs text-muted-foreground">
                                    {index + 1}
                                </div>
                                <div className="card-list-term">
                                    <input
                                        type="text"
                                        value={card.term}
                                        onChange={(e) => updateCard(card.id, 'term', e.target.value)}
                                        placeholder="Term"
                                        data-testid={`card-term-${index}`}
                                        className="w-full input-minimal font-sans"
                                    />
                                </div>
                                <div className="card-list-definition">
                                    <input
                                        type="text"
                                        value={card.definition}
                                        onChange={(e) => updateCard(card.id, 'definition', e.target.value)}
                                        placeholder="Definition"
                                        data-testid={`card-definition-${index}`}
                                        className="w-full input-minimal font-sans text-muted-foreground"
                                    />
                                </div>
                                <div className="card-list-actions">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeCard(card.id, card.isNew)}
                                        disabled={cards.length <= 1}
                                        data-testid={`delete-card-${index}`}
                                        className="text-muted-foreground hover:text-destructive"
                                    >
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Card Button */}
                    <div className="mt-8">
                        {isAtLimit ? (
                            <div className="p-4 border border-dashed border-warning/50 bg-warning/5 text-warning text-sm flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
                                Maximum of {MAX_CARDS} cards reached
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                onClick={addCard}
                                data-testid="add-card-button"
                                className="w-full rounded-none border-dashed h-14 btn-lift"
                            >
                                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Add Card
                            </Button>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
